import type { LogicalType } from '@/types'
import type { DataTypeDefinition, DialectAdapter, DialectWarning } from '../dialectTypes'

const DATA_TYPES: DataTypeDefinition[] = [
  { value: 'TINYINT',          label: 'TINYINT',          logicalType: 'smallint' },
  { value: 'SMALLINT',         label: 'SMALLINT',         logicalType: 'smallint' },
  { value: 'INT',              label: 'INT',               logicalType: 'integer' },
  { value: 'BIGINT',           label: 'BIGINT',            logicalType: 'bigint' },
  { value: 'DECIMAL(10,2)',    label: 'DECIMAL(p,s)',      logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'NUMERIC(10,2)',    label: 'NUMERIC(p,s)',      logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'FLOAT',            label: 'FLOAT',             logicalType: 'float' },
  { value: 'REAL',             label: 'REAL',              logicalType: 'float' },
  { value: 'VARCHAR(255)',     label: 'VARCHAR(n)',         logicalType: 'string', hasLength: true },
  { value: 'NVARCHAR(255)',    label: 'NVARCHAR(n)',        logicalType: 'string', hasLength: true },
  { value: 'CHAR(1)',          label: 'CHAR(n)',            logicalType: 'string', hasLength: true },
  { value: 'NCHAR(1)',         label: 'NCHAR(n)',           logicalType: 'string', hasLength: true },
  { value: 'TEXT',             label: 'TEXT',              logicalType: 'text' },
  { value: 'NVARCHAR(MAX)',    label: 'NVARCHAR(MAX)',      logicalType: 'text' },
  { value: 'BIT',              label: 'BIT',               logicalType: 'boolean' },
  { value: 'DATE',             label: 'DATE',              logicalType: 'date' },
  { value: 'TIME',             label: 'TIME',              logicalType: 'time' },
  { value: 'DATETIME2',        label: 'DATETIME2',         logicalType: 'datetime' },
  { value: 'DATETIMEOFFSET',   label: 'DATETIMEOFFSET',    logicalType: 'datetime' },
  { value: 'UNIQUEIDENTIFIER', label: 'UNIQUEIDENTIFIER',  logicalType: 'uuid' },
  { value: 'NVARCHAR(MAX)',    label: 'NVARCHAR(MAX) (JSON)',logicalType: 'json' },
  { value: 'VARBINARY(MAX)',   label: 'VARBINARY(MAX)',     logicalType: 'binary' },
]

const LOGICAL_TO_PHYSICAL: Record<LogicalType, string> = {
  string:   'NVARCHAR(255)',
  text:     'NVARCHAR(MAX)',
  integer:  'INT',
  bigint:   'BIGINT',
  smallint: 'SMALLINT',
  decimal:  'DECIMAL(10,2)',
  float:    'FLOAT',
  boolean:  'BIT',
  date:     'DATE',
  time:     'TIME',
  datetime: 'DATETIME2',
  uuid:     'UNIQUEIDENTIFIER',
  json:     'NVARCHAR(MAX)',
  binary:   'VARBINARY(MAX)',
}

function normalizeTypeKey(type: string): string {
  return type.trim().toUpperCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
}

export const sqlServerDialect: DialectAdapter = {
  id: 'sqlserver',
  label: 'SQL Server',
  dataTypes: DATA_TYPES,

  mapLogicalToPhysical(type) {
    return LOGICAL_TO_PHYSICAL[type] ?? 'NVARCHAR(MAX)'
  },

  mapPhysicalToLogical(type) {
    const key = normalizeTypeKey(type)
    switch (key) {
      case 'TINYINT':
      case 'SMALLINT':
        return 'smallint'
      case 'INT':
        return 'integer'
      case 'BIGINT':
        return 'bigint'
      case 'DECIMAL':
      case 'NUMERIC':
      case 'MONEY':
      case 'SMALLMONEY':
        return 'decimal'
      case 'FLOAT':
      case 'REAL':
        return 'float'
      case 'VARCHAR':
      case 'NVARCHAR':
      case 'CHAR':
      case 'NCHAR':
        return 'string'
      case 'TEXT':
      case 'NTEXT':
        return 'text'
      case 'BIT':
        return 'boolean'
      case 'DATE':
        return 'date'
      case 'TIME':
        return 'time'
      case 'DATETIME':
      case 'DATETIME2':
      case 'DATETIMEOFFSET':
      case 'SMALLDATETIME':
        return 'datetime'
      case 'UNIQUEIDENTIFIER':
        return 'uuid'
      case 'BINARY':
      case 'VARBINARY':
      case 'IMAGE':
        return 'binary'
      default:
        return 'text'
    }
  },

  validateColumn(column) {
    const warnings: DialectWarning[] = []
    const upper = column.type.trim().toUpperCase()
    if (upper === 'JSONB' || upper === 'JSON') {
      warnings.push({ column: column.name, message: 'JSON/JSONB does not exist in SQL Server; use NVARCHAR(MAX).', severity: 'warning' })
    }
    if (upper === 'UUID') {
      warnings.push({ column: column.name, message: 'UUID is not a SQL Server type; use UNIQUEIDENTIFIER.', severity: 'warning' })
    }
    if (upper === 'BOOLEAN' || upper === 'BOOL') {
      warnings.push({ column: column.name, message: 'BOOLEAN is not a SQL Server type; use BIT.', severity: 'warning' })
    }
    return warnings
  },
}
